import React, { FC } from 'react';

import { FormattedMessage } from 'react-intl';

import { CoreUtils, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import { TeamDisplay } from '@nhl/nhl-react-ui/lib/TeamDisplay';

import CommonMessages from '~/core/commonMessages';
import { parseValueFromLocalizedAttribute } from '~/core/utils';
import ActivityIndicator from '~/views/components/activityIndicator/ActivityIndicator';
import LocalizedTeamName from '~/views/components/localizedTeamName/LocalizedTeamName';
import { StrongName, WeakName } from '~/views/components/localizedTeamName/styles';
import { DraftPickState } from '~/widgets/draftMiniTracker/enums';

import { DraftPick } from '../../types';

import {
    ActivityIndicatorContainer,
    DraftPickContainer,
    Forfeited,
    InnerDraftPick,
    OnTheClock,
    Pick,
    PickChip,
    PickChipRow,
    Position,
    TeamAndPick,
} from './styles';

type Props = {
    accentColor: string;
    isActive: boolean;
    language: ShortCodeEnums.Language;
    pick: DraftPick;
};

const DraftPickSlide: FC<Props> = ({ accentColor, isActive, language, pick }) => (
    <DraftPickContainer>
        <InnerDraftPick $accentColor={accentColor} $isActive={isActive}>
            <PickChipRow>
                <PickChip $accentColor={accentColor} $isActive={isActive}>
                    <FormattedMessage
                        description="Message stating which overall pick number the draft pick is, eg Pick 3"
                        defaultMessage="Pick {pickNumber}"
                        values={{ pickNumber: pick.overallPick }}
                    />
                </PickChip>
                {pick.state === DraftPickState.ON_THE_CLOCK && (
                    <ActivityIndicatorContainer>
                        <ActivityIndicator />
                    </ActivityIndicatorContainer>
                )}
            </PickChipRow>
            <TeamAndPick>
                {/* TODO: GET RID OF THIS HEINOUS OVERRIDE URL -
                    new teams want their new logos but draft is for last season so new logos won't show -
                    this forces the matter until we can come up with something better
                */}
                <TeamDisplay
                    logoUrlOverride={`https://assets.nhle.com/logos/nhl/svg/${pick.teamAbbrev}_dark.svg`}
                    team={{ triCode: pick.teamAbbrev }}
                    displayName={false}
                />
                {pick.state === DraftPickState.CONFIRMED && pick.positionCode ? (
                    <Pick>
                        <WeakName>{parseValueFromLocalizedAttribute(pick.firstName, language)}</WeakName>
                        <StrongName>{parseValueFromLocalizedAttribute(pick.lastName, language)}</StrongName>
                        <Position>
                            <CoreUtils.SafeFormattedMessage
                                message={CommonMessages.playerInfoDescriptions.abbreviations[pick.positionCode]}
                            />
                        </Position>
                    </Pick>
                ) : (
                    <Pick>
                        <LocalizedTeamName
                            teamPlaceNameWithPreposition={pick.teamPlaceNameWithPreposition}
                            teamId={pick.teamId}
                            teamCommonName={pick.teamCommonName}
                        />
                        {pick.state === DraftPickState.ON_THE_CLOCK && (
                            <OnTheClock $accentColor={accentColor}>
                                <FormattedMessage
                                    description="Message explaining that a team is the next team to make a draft pick."
                                    defaultMessage="On the Clock"
                                />
                            </OnTheClock>
                        )}
                        {pick.state === DraftPickState.FORFEIT && (
                            <Forfeited>
                                <FormattedMessage
                                    description="Message explaining that this draft pick was forfeited."
                                    defaultMessage="Forfeited"
                                />
                            </Forfeited>
                        )}
                    </Pick>
                )}
            </TeamAndPick>
        </InnerDraftPick>
    </DraftPickContainer>
);

export default DraftPickSlide;
